/**
 * Badge Color Utilities
 *
 * Provides consistent color mapping for commit classifications and complexity scores
 * to match the colors used in charts throughout the application.
 */

import type { ComplexityClass } from "../types/data"

/**
 * Get badge variant for commit classification
 * Maps to chart colors (chart-1 through chart-5)
 */
export function getClassificationBadgeColor(classification?: string): string {
  switch (classification) {
    case "feature":
      return "hsl(var(--chart-1))" // Orange
    case "bugfix":
      return "hsl(var(--chart-2))" // Teal
    case "refactoring":
      return "hsl(var(--chart-3))" // Blue
    case "ci":
    case "tests":
      return "hsl(var(--chart-4))" // Yellow
    case "docs":
    case "chore":
      return "hsl(var(--chart-5))" // Light Orange
    case "perf":
      return "hsl(280 65% 60%)" // Purple
    case "revert":
      return "hsl(340 75% 55%)" // Pink
    case "merge":
      return "hsl(160 60% 50%)" // Green
    case "archive":
      return "hsl(30 80% 55%)" // Dark Orange
    case "style":
    case "styling":
      return "hsl(200 70% 50%)" // Bright Blue
    default:
      return "hsl(0 0% 50%)" // Gray for unclassified
  }
}

/**
 * Get badge color for complexity class (size)
 * Maps to chart colors to match stacked bar charts
 */
export function getComplexityBadgeColor(complexityClass: ComplexityClass): string {
  switch (complexityClass) {
    case "XS":
      return "hsl(var(--chart-1))" // Orange
    case "S":
      return "hsl(var(--chart-2))" // Teal
    case "M":
      return "hsl(var(--chart-3))" // Blue
    case "L":
      return "hsl(var(--chart-4))" // Yellow
    case "XL":
      return "hsl(var(--chart-5))" // Light Orange
    default:
      return "hsl(0 0% 50%)" // Gray fallback
  }
}

/**
 * Get badge color for score ranges
 * Higher scores get warmer colors (better/more complex)
 */
export function getScoreBadgeColor(score: number): string {
  if (score >= 80) return "hsl(var(--chart-1))" // Orange - High
  if (score >= 60) return "hsl(var(--chart-4))" // Yellow - Medium-High
  if (score >= 40) return "hsl(var(--chart-2))" // Teal - Medium
  if (score >= 20) return "hsl(var(--chart-3))" // Blue - Low-Medium
  return "hsl(var(--chart-5))" // Light Orange - Low
}
